import * as managementActions from 'client/actions/actionsManagement'
import { Select } from 'client/components/core/Sidebar/components/Settings/components'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { FlexAlignStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useManagement, {
  selectors as managementSelectors,
} from 'client/hooks/useManagement'
import { usePage } from 'client/store'
import { pageSelectors } from 'client/store/page/pageSelectors'
import { MobileMenuTypesEnum } from 'common/components/entities/Menu/enums/MobileMenuTypesEnum'
import { hasSubMenu } from 'common/components/entities/Menu/utils/hasSubMenu'
import { useActions } from 'common/hooks/useActions'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { HorizontalLine } from 'src/editor/layout/sidebar/settings/horizontal-line'
import { useCalculateTypography } from 'src/editor/shared/hooks/use-calculate-typography'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { ToolbarButton } from 'src/shared/components/toolbar-button'
import { MenuInterface } from 'src/shared/types/entities/menu'
import { FlexTypographyGroup } from '../groups/typography-group/flex-typography-group'
import { MenuBurger } from './components/menu-burger'
import { MenuItem } from './components/menu-item'
import { MenuSpacing } from './components/menu-spacing'
import {
  allowedBlogLinksPageTypes,
  mobileMenuTypesLocoKeys,
} from './constants/menu-settings-consts'
import { useUpdateMenuItem } from './hooks/use-update-menu-item'

export interface CommonMenuSettingsProps {
  entity: MenuInterface
  defaultEntity?: MenuInterface
  isWebsite?: boolean
  isAllowedWebsiteHeaderEntity?: boolean
}

export const CommonMenuSettings = ({
  entity,
  defaultEntity,
  isWebsite,
  isAllowedWebsiteHeaderEntity,
}: CommonMenuSettingsProps) => {
  const { t } = useTranslation()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<MenuInterface>()
  const { isMobile } = useDeviceModeContext()
  const pageType = usePage(pageSelectors.getPageType)
  const isSideMenuOpened = useManagement(managementSelectors.isSideMenuOpened)
  const isDropDownMenuOpened = useManagement(
    managementSelectors.isDropDownMenuOpened,
  )
  const [toggleSideMenuOpened, toggleDropDownMenuOpened] = useActions([
    managementActions.toggleSideMenuOpened,
    managementActions.toggleDropDownMenuOpened,
  ])
  const { globalFontFamily, globalMobileFontFamily } = useCalculateTypography()

  const { addMenuItem } = useUpdateMenuItem({ entity })

  useEffect(() => {
    if (!allowedBlogLinksPageTypes.includes(pageType)) {
      if (entity.menuItems.some(item => item.link.includes('%BLOG_'))) {
        const updatedMenuItems = entity.menuItems.map(item => {
          if (item.link.includes('%BLOG_')) {
            item.link = ''
            if (
              item.subMenuItems?.some(subItem =>
                subItem.link.includes('%BLOG_'),
              )
            ) {
              item.subMenuItems = item.subMenuItems?.map(subItem => {
                if (subItem.link.includes('%BLOG_')) {
                  subItem.link = ''
                }
                return subItem
              })
            }
          }
          return item
        })
        updateProp('menuItems')(updatedMenuItems)
      }
    }
  }, [entity.menuItems, pageType, updateProp])

  const entityHasSubMenu = useMemo(
    () => entity.menuItems.some(item => hasSubMenu(item)),
    [entity.menuItems],
  )

  useEffect(() => {
    if (entityHasSubMenu) {
      if (entity.subMenuSpacing === undefined) {
        updateProp('subMenuSpacing')(15)
      }
      if (entity.mobileSubMenuSpacing === undefined) {
        updateProp('mobileSubMenuSpacing')(15)
      }
      if (!entity.subMenuColor) {
        updateProp('subMenuColor')('rgba(74, 74, 74, 1)')
      }
      if (!entity.activeSubmenuColor) {
        updateProp('activeSubmenuColor')('rgba(74, 74, 74, 1)')
      }
    }
  }, [
    entity.activeSubmenuColor,
    entity.mobileSubMenuSpacing,
    entity.subMenuColor,
    entity.subMenuSpacing,
    entityHasSubMenu,
    updateProp,
  ])

  return (
    <div>
      <MenuBurger
        update={updateProp}
        entity={entity}
        isShowCheckbox={isWebsite && !isAllowedWebsiteHeaderEntity}
      />
      {isMobile && entity.burger && (
        <React.Fragment>
          <FlexAlignStyle
            label="entity_settings.menu.item_align"
            mobileAlign={entity.burgerAlignSelf || 'flex-end'}
            mobileUpdate={updateProp('burgerAlignSelf')}
          />
          <Select
            choiceList={mobileMenuTypesLocoKeys}
            update={updateProp('mobileMenuType')}
            value={entity.mobileMenuType}
          />
          {entity.mobileMenuType === MobileMenuTypesEnum.side && (
            <ColorPickerControl
              update={updateProp('sideMenuBackgroundColor')}
              color={entity.sideMenuBackgroundColor}
              label="entity_settings.menu.item.side_menu_background_color"
              singleColorMode
              isClearable={isWebsite}
            />
          )}
          {entity.mobileMenuType === MobileMenuTypesEnum.side && (
            <Switcher
              btnList={[
                {
                  value: true,
                  content: 'entity_settings.menu.item.emulate_side_menu_opened',
                },
                {
                  value: false,
                  content: 'entity_settings.menu.item.emulate_side_menu_closed',
                },
              ]}
              value={isSideMenuOpened}
              labelText={
                'entity_settings.menu.item.emulate_side_menu_description'
              }
              update={toggleSideMenuOpened}
            />
          )}
          {entity.mobileMenuType === MobileMenuTypesEnum.dropDown && (
            <Switcher
              className="mt-[16px]"
              btnList={[
                {
                  value: true,
                  content:
                    'entity_settings.menu.item.emulate_drop_down_menu_opened',
                },
                {
                  value: false,
                  content:
                    'entity_settings.menu.item.emulate_drop_down_menu_closed',
                },
              ]}
              value={isDropDownMenuOpened}
              labelText={
                'entity_settings.menu.item.emulate_drop_down_menu_description'
              }
              update={toggleDropDownMenuOpened}
            />
          )}
        </React.Fragment>
      )}
      {entity.menuItems.map((item, index, arr) => (
        <React.Fragment key={`${entity.id}-${index}`}>
          <MenuItem
            index={index}
            item={item}
            entity={entity}
            itemsLength={arr.length}
            isAllowedBlogLinks={allowedBlogLinksPageTypes.includes(pageType)}
            isAllowedWebsiteLinks={!!isWebsite}
          />
          {index + 1 < arr.length && <HorizontalLine />}
        </React.Fragment>
      ))}
      <ToolbarButton onClick={addMenuItem} className="!mt-4 w-full">
        {t('entity_settings.menu.item.add')}
      </ToolbarButton>
      <FlexTypographyGroup
        title="typography"
        entity={entity}
        updateEntity={updateEntity}
        alignSelfKey="alignSelf"
        letterSpacingKey="letterSpacing"
        lineHeightKey="lineHeight"
        fontFamilyKey="fontFamily"
        fontWeightKey="fontWeight"
        fontStyleKey="fontStyle"
        fontFileIdKey="fontFileId"
        mobileLineHeightKey="mobileLineHeight"
        mobileAlignSelfKey="mobileAlignSelf"
        mobileLetterSpacingKey="mobileLetterSpacing"
        mobileFontFamilyKey="mobileFontFamily"
        mobileFontWeightKey="mobileFontWeight"
        mobileFontStyleKey="mobileFontStyle"
        mobileFontFileIdKey="mobileFontFileId"
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        alignSelf={entity.alignSelf}
        mobileAlignSelf={entity.mobileAlignSelf}
        letterSpacing={entity.letterSpacing}
        mobileLetterSpacing={entity.mobileLetterSpacing}
        fontFamily={entity.fontFamily}
        mobileFontFamily={entity.mobileFontFamily}
        fontWeight={entity.fontWeight}
        mobileFontWeight={entity.mobileFontWeight}
        fontStyle={entity.fontStyle}
        mobileFontStyle={entity.mobileFontStyle}
        globalFontFamily={globalFontFamily}
        mobileGlobalFontFamily={globalMobileFontFamily}
        fontSizeKey="fontSize"
        mobileFontSizeKey="mobileFontSize"
        fontSize={entity.fontSize}
        updateLineHeight={updateProp('lineHeight')}
        updateMobileLineHeight={updateProp('mobileLineHeight')}
        updateAlignSelf={updateProp('alignSelf')}
        updateMobileAlignSelf={updateProp('mobileAlignSelf')}
        updateLetterSpacing={updateProp('letterSpacing')}
        updateMobileLetterSpacing={updateProp('mobileLetterSpacing')}
        mobileFontSize={entity.mobileFontSize}
        updateFontSize={updateProp('fontSize')}
        updateMobileFontSize={updateProp('mobileFontSize')}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'color',
                'activeColor',
                'subMenuColor',
                'burgerIconColor',
                'itemUnderlineColor',
                'sideMenuBackgroundColor',
                'activeSubmenuColor',
              ]}
              mobileKeys={[
                'mobileColor',
                'mobileActiveColor',
                'mobileItemUnderlineColor',
              ]}
            />
          )
        }
      >
        color
      </GroupTitle>
      <ColorPickerControl
        update={updateProp('color')}
        mobileUpdate={updateProp('mobileColor')}
        color={entity.color}
        mobileColor={entity.mobileColor}
        label="entity_settings.menu.item.color"
      />
      <ColorPickerControl
        update={updateProp('activeColor')}
        mobileUpdate={updateProp('mobileActiveColor')}
        color={entity.activeColor}
        mobileColor={entity.mobileActiveColor}
        label="entity_settings.menu.item.active"
      />
      <ColorPickerControl
        update={updateProp('itemUnderlineColor')}
        mobileUpdate={updateProp('mobileItemUnderlineColor')}
        color={entity.itemUnderlineColor}
        mobileColor={entity.mobileItemUnderlineColor}
        label="entity_settings.menu.item_underline.active_color"
        isClearable
      />
      <MenuSpacing
        spacing={entity.spacing}
        mobileSpacing={entity.mobileSpacing}
        update={updateProp('spacing')}
        updateMobile={updateProp('mobileSpacing')}
        subMenuSpacing={entity.subMenuSpacing}
        mobileSubMenuSpacing={entity.mobileSubMenuSpacing}
        updateSubMenu={updateProp('subMenuSpacing')}
        updateMobileSubMenu={updateProp('mobileSubMenuSpacing')}
        hasSubMenu={entityHasSubMenu}
      />
      {entityHasSubMenu && !isMobile && (
        <>
          <ColorPickerControl
            update={updateProp('subMenuColor')}
            color={entity.subMenuColor}
            label="entity_settings.menu.item.sub_item_color"
            singleColorMode
            isClearable
          />
          <ColorPickerControl
            update={updateProp('activeSubmenuColor')}
            color={entity.activeSubmenuColor}
            label="entity_settings.menu.item.sub_item_active_color"
            singleColorMode
            isClearable
          />
          <ColorPickerControl
            update={updateProp('subMenuBackgroundColor')}
            color={entity.subMenuBackgroundColor}
            label="entity_settings.menu.item.sub_item_background_color"
            singleColorMode
            isClearable
          />
        </>
      )}
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      {!isWebsite ||
        (!isAllowedWebsiteHeaderEntity && (
          <DeviceAppearanceNew entity={entity} />
        ))}
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </div>
  )
}
